// How the map tints a step by the time spent in it: a sequential ramp, plus the label colours
// that stay legible on whatever grade a cell lands on.
import type { ProcessGraphNode } from "./processGraphModel";
import { totalDuration } from "./processGraphModel";

// Calm desaturated-blue sequential ramp (white -> app blue-400 #6e9ad0) for time-on-step.
// It intentionally stays light enough that labels remain readable while making slower steps
// increasingly prominent.
const HEAT_STOPS: [number, [number, number, number]][] = [
  [0.0, [255, 255, 255]],
  [0.25, [232, 240, 247]],
  [0.5, [200, 220, 238]],
  [0.75, [157, 188, 221]],
  [1.0, [110, 154, 208]],
];

const heatColor = (t: number): string => {
  const clamped = Math.max(0, Math.min(1, t));
  for (let i = 1; i < HEAT_STOPS.length; i++) {
    if (clamped <= HEAT_STOPS[i][0]) {
      const [t0, c0] = HEAT_STOPS[i - 1];
      const [t1, c1] = HEAT_STOPS[i];
      const f = t1 === t0 ? 0 : (clamped - t0) / (t1 - t0);
      const c = [0, 1, 2].map(j => Math.round(c0[j] + (c1[j] - c0[j]) * f));
      return `rgb(${c[0]},${c[1]},${c[2]})`;
    }
  }
  return "rgb(110,154,208)";
};

/** Build the shared heat scale from time spent across every occurrence of each visible step. */
export const totalDurationHeatScale = (
  nodes: ProcessGraphNode[],
): { min: number; max: number; colorById: Map<string, string> } | null => {
  if (nodes.length === 0) return null;
  const totals = nodes.map(totalDuration);
  const min = Math.min(...totals);
  const max = Math.max(...totals);
  return {
    min,
    max,
    colorById: new Map(
      nodes.map(node => [
        node.id,
        heatColor(max > min ? (totalDuration(node) - min) / (max - min) : 0),
      ]),
    ),
  };
};

// Keep labels readable on the generated heat fill. The restored blue ramp resolves to the dark
// pair, while retaining the original fallback if the ramp is changed to a darker color later.
const relativeLuminance = (r: number, g: number, b: number): number => {
  const channels = [r, g, b].map(value => {
    const normalized = value / 255;
    return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
};
const contrastRatio = (first: number, second: number) =>
  (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
const DARK_LABEL_LUMINANCE = relativeLuminance(26, 29, 36);
export const labelColors = (rgb: string): { title: string; sub: string } => {
  const match = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/.exec(rgb);
  const luminance = match
    ? relativeLuminance(Number(match[1]), Number(match[2]), Number(match[3]))
    : 1;
  return contrastRatio(luminance, DARK_LABEL_LUMINANCE) >= contrastRatio(luminance, 1)
    ? { title: "#1a1d24", sub: "#2b3038" }
    : { title: "#ffffff", sub: "rgba(255,255,255,0.9)" };
};
